import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, Ban } from 'lucide-react';
import { Modal } from './ui';
import { Collection } from '../types/collections';

interface NoteCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collections: Collection[];
  currentCollectionId: number | null;
  onSave: (nextCollectionId: number | null) => Promise<void> | void;
  isSaving?: boolean;
  errorMessage?: string | null;
}

const TITLE_ID = 'note-collection-modal-title';

// Subsequence + substring fuzzy matcher. Higher = better. `null` = no match.
function fuzzyScore(query: string, target: string): number | null {
  if (!query) return 1;
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  const idx = t.indexOf(q);
  if (idx !== -1) return 100 - idx; // substring wins, earlier positions rank higher
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  if (qi !== q.length) return null;
  return 10 - (t.length - q.length) / Math.max(1, t.length); // subsequence match
}

export const NoteCollectionModal: React.FC<NoteCollectionModalProps> = ({
  isOpen,
  onClose,
  collections,
  currentCollectionId,
  onSave,
  isSaving = false,
  errorMessage = null,
}) => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(currentCollectionId);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedId(currentCollectionId);
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen, currentCollectionId]);

  const filtered = useMemo(() => {
    const scored = collections
      .map((c) => ({ collection: c, score: fuzzyScore(query.trim(), c.name) }))
      .filter((x): x is { collection: Collection; score: number } => x.score !== null);
    scored.sort((a, b) => b.score - a.score || a.collection.name.localeCompare(b.collection.name));
    return scored.map((x) => x.collection);
  }, [collections, query]);

  const handleToggle = (id: number): void => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const isDirty = selectedId !== currentCollectionId;

  const handleSave = async (): Promise<void> => {
    if (!isDirty) return;
    await onSave(selectedId);
  };

  const summary = (() => {
    if (selectedId === null && currentCollectionId === null) return 'No collection selected.';
    if (selectedId === null) return 'Will be removed from its current collection on save.';
    const picked = collections.find((c) => c.id === selectedId);
    if (!picked) return 'Collection selected.';
    if (selectedId === currentCollectionId) return `Currently in "${picked.name}".`;
    return `Will be moved to "${picked.name}".`;
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isBusy={isSaving}
      size="md"
      labelledBy={TITLE_ID}
    >
      <Modal.Header
        eyebrow="Organize"
        title="Move to a collection"
        titleId={TITLE_ID}
        subtitle="Pick one collection for this note. Click the current selection to clear it."
      />

      <Modal.Body>
        <div className="relative flex items-center">
          <Search size={14} className="pointer-events-none absolute left-3.5 text-on-surface-variant opacity-55" />
          <input
            ref={searchRef}
            type="text"
            className="pb-modal-input pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search collections"
            disabled={isSaving}
            autoComplete="off"
          />
        </div>

        <div
          className="flex max-h-[22rem] flex-col gap-2 overflow-y-auto pr-1"
          role="listbox"
          aria-label="Collections"
        >
          {collections.length === 0 && (
            <p className="m-0 px-2 py-6 text-center text-sm text-on-surface-variant opacity-70">
              You don't have any collections yet. Close this modal and create one from the Library page.
            </p>
          )}

          {collections.length > 0 && filtered.length === 0 && (
            <p className="m-0 px-2 py-6 text-center text-sm text-on-surface-variant opacity-70">
              No collections match "{query}".
            </p>
          )}

          {filtered.map((collection) => {
            const isSelected = selectedId === collection.id;
            const wasCurrent = currentCollectionId === collection.id;

            const isRemoved = wasCurrent && !isSelected;
            const rowClasses = [
              'grid grid-cols-[auto_1fr_auto] items-center gap-3.5 text-left',
              'cursor-pointer rounded-md border px-3.5 py-3 font-body text-on-surface',
              'transition-[background,border-color,color,transform,box-shadow] duration-200 ease-out',
              'hover:enabled:translate-x-0.5 hover:enabled:border-accent',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              'disabled:cursor-not-allowed disabled:opacity-60',
              isSelected
                ? 'border-accent bg-surface-highlight shadow-[0_2px_12px_rgba(91,63,208,0.08)]'
                : isRemoved
                  ? 'border-dashed border-outline-variant bg-surface-container text-on-surface-variant'
                  : 'border-outline-variant bg-surface',
            ].join(' ');

            const checkClasses = [
              'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
              'transition-[background,border-color,color,transform] duration-200 ease-out',
              isSelected
                ? 'scale-[1.08] border-accent bg-primary text-white'
                : 'border-outline-variant bg-surface-container-lowest text-transparent',
            ].join(' ');

            return (
              <button
                key={collection.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={rowClasses}
                onClick={() => handleToggle(collection.id)}
                disabled={isSaving}
              >
                <span className={checkClasses} aria-hidden>
                  <Check size={14} strokeWidth={3} />
                </span>
                <span className="flex min-w-0 flex-col gap-[0.2rem]">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.9375rem] font-semibold tracking-[-0.005em]">
                    {collection.name}
                  </span>
                  {collection.description && (
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] text-on-surface-variant opacity-75">
                      {collection.description}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 flex-col items-end gap-[0.35rem]">
                  {isRemoved && (
                    <span className="inline-flex animate-note-collection-tag-in items-center gap-1 rounded-sm border border-dashed border-outline-variant bg-transparent px-2 py-[0.2rem] text-[0.625rem] font-bold uppercase tracking-[0.12em] text-on-surface-variant">
                      <Ban size={11} /> Removing
                    </span>
                  )}
                  {isSelected && !wasCurrent && (
                    <span className="inline-flex animate-note-collection-tag-in items-center gap-1 rounded-sm border border-accent bg-surface-highlight px-2 py-[0.2rem] text-[0.625rem] font-bold uppercase tracking-[0.12em] text-accent">
                      New
                    </span>
                  )}
                  {wasCurrent && isSelected && (
                    <span className="inline-flex animate-note-collection-tag-in items-center gap-1 rounded-sm bg-primary px-2 py-[0.2rem] text-[0.625rem] font-bold uppercase tracking-[0.12em] text-white">
                      Current
                    </span>
                  )}
                  <span className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-on-surface-variant opacity-60">
                    {collection.note_count} {collection.note_count === 1 ? 'note' : 'notes'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <p className="m-0 mt-1 text-[0.8125rem] italic text-on-surface-variant opacity-85">
          {summary}
        </p>
        {errorMessage && <p className="pb-modal-error" role="alert">{errorMessage}</p>}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="pb-modal-btn pb-modal-btn--ghost"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="pb-modal-btn pb-modal-btn--primary"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
